import {
	TaxRegion,
	TaxCategory,
	TaxCodeLevel,
	Commission,
	CommissionType,
	CommissionValues
} from "@/Contracts/Users";
import {
	QuoteBlockData,
	QuoteProduct,
	QuoteMargin
} from "@/Contracts/ProposalBuilder/Block";
import {
	ProductBillingType,
	ProductType,
	ProductTaxType
} from "@/Contracts/Products";
import { Document, QuoteTotal } from "@/Contracts/Documents";
import { roundTwoDecimals } from "@/Utils";

export interface SubTotalResult {
	subTotal: number;
	setupPriceTotal: number;
	margin: number;
	marginPercentage: number;
	finalSellPrice: number;
	totalSellPrice: number;
	taxableSellPrice: number;
	totalCostPrice: number;
}

export const calculateSubTotal = (
	productData: QuoteProduct
): SubTotalResult => {
	const {
		selected_setup_price,
		selected_cost_price,
		selected_setup_discount,
		sell_price,
		quantity,
		discount,
		is_setup_fee_linked_to_quantity,
		should_add_setup_price_to_margin,
		product_type,
		show_individual_product_pricing,
		bundled_products = []
	} = productData;

	let setupPriceTotal = is_setup_fee_linked_to_quantity
		? quantity > 0
			? selected_setup_price * quantity -
			  selected_setup_discount * quantity
			: 0
		: quantity > 0
		? selected_setup_price - selected_setup_discount
		: 0;
	let totalSellPrice = roundTwoDecimals(sell_price) * quantity;
	let selectedCostPrice = roundTwoDecimals(selected_cost_price);
	let taxableSellPrice = roundTwoDecimals(sell_price) * quantity;

	// check if bundle and has individual pricing,
	const isBundleProductWithIndividualPricing =
		product_type === ProductType.Bundle && show_individual_product_pricing;

	if (isBundleProductWithIndividualPricing) {
		// removing unselected optional products
		const validBundledProducts = (bundled_products || []).filter(
			a => a.tax_category_id && (a.is_optional ? a.is_selected : true)
		);
		// adding the bundled items setup fee to the product setup fee
		setupPriceTotal += validBundledProducts.reduce(
			(total, curr) => total + (curr.selected_setup_price || 0),
			0
		);

		// overriding the sell price tot he bundled item sell price.
		totalSellPrice = validBundledProducts.reduce(
			(total, curr) =>
				total +
				(roundTwoDecimals(curr.sell_price) || 0) * curr.quantity,
			0
		);

		taxableSellPrice = validBundledProducts.reduce((total, curr) => {
			if (!curr.tax_category_id) {
				return total;
			}
			return (
				total + (roundTwoDecimals(curr.sell_price) || 0) * curr.quantity
			);
		}, 0);

		// overriding the sell price to the bundled item sell price.
		selectedCostPrice = validBundledProducts.reduce(
			(total, curr) =>
				total +
				(roundTwoDecimals(curr.selected_cost_price) || 0) *
					curr.quantity,
			0
		);
	}

	const totalCostPrice = selectedCostPrice * quantity;

	const finalSellPrice = totalSellPrice - discount * quantity;

	taxableSellPrice = taxableSellPrice - discount * quantity;

	const subTotal = !!should_add_setup_price_to_margin
		? finalSellPrice + setupPriceTotal
		: finalSellPrice;

	const margin = Math.round((subTotal - totalCostPrice) * 100) / 100;
	const marginPercentage =
		(!!subTotal && Math.round((margin / subTotal) * 100 * 100) / 100) || 0;

	return {
		finalSellPrice: roundTwoDecimals(finalSellPrice),
		margin: roundTwoDecimals(margin),
		marginPercentage: roundTwoDecimals(marginPercentage),
		setupPriceTotal: roundTwoDecimals(setupPriceTotal),
		subTotal: roundTwoDecimals(subTotal),
		totalCostPrice: roundTwoDecimals(totalCostPrice),
		taxableSellPrice: roundTwoDecimals(taxableSellPrice),
		totalSellPrice: roundTwoDecimals(totalSellPrice)
	};
};

export const calculateCommissions = (
	productData: QuoteProduct,
	commission?: Commission[]
) => {
	if (!commission || commission?.length < 0) return 0;
	const selectedCostPrice = productData.selected_cost_price ?? 0;
	const costPrice = selectedCostPrice * productData.quantity;
	const updatedSalePrice = calculateSubTotal(productData).subTotal;
	const margin = updatedSalePrice - costPrice;
	const marginPercentage = (margin / updatedSalePrice) * 100;
	const commissionType = commission[0].commission_type;
	const selectedCommission = commission[0][commissionType];
	let validScales: CommissionValues | undefined;
	if (selectedCommission)
		switch (commission[0].commission_type) {
			case CommissionType.PercentageOfSalePrice:
				if (typeof selectedCommission === "number")
					return (updatedSalePrice * (selectedCommission ?? 0)) / 100;
				else
					return (
						(updatedSalePrice *
							(selectedCommission[0]?.percentage ?? 0)) /
						100
					);
			case CommissionType.PercentageOfMargin:
				if (typeof selectedCommission === "number")
					return (margin * selectedCommission) / 100;
				else
					return (
						(margin * (selectedCommission[0]?.percentage ?? 0)) /
						100
					);
			case CommissionType.SlidingScaleOnMargin:
				validScales = selectedCommission.find(
					scale =>
						typeof scale.from !== "undefined" &&
						typeof scale.to !== "undefined" &&
						!isNaN(marginPercentage) &&
						marginPercentage >= scale.from &&
						marginPercentage < scale.to
				);
				return validScales?.percentage
					? (validScales.percentage * margin) / 100
					: 0;
			case CommissionType.SlidingScaleOnSalePrice:
				validScales = selectedCommission.find(
					scale =>
						typeof scale.from !== "undefined" &&
						typeof scale.to !== "undefined" &&
						!isNaN(marginPercentage) &&
						marginPercentage >= scale.from &&
						marginPercentage < scale.to
				);
				return validScales?.percentage
					? (validScales.percentage * updatedSalePrice) / 100
					: 0;
		}
	return 0;
};

export const calculateFlatTaxSubTotal = (
	productData: QuoteProduct
): SubTotalResult => {
	const {
		selected_setup_price,
		selected_cost_price,
		selected_setup_discount,
		sell_price,
		quantity,
		discount,
		should_add_setup_price_to_margin,
		product_type,
		show_individual_product_pricing,
		bundled_products = []
	} = productData;

	let setupPriceTotal =
		quantity > 0 ? selected_setup_price - selected_setup_discount : 0;
	let totalSellPrice = roundTwoDecimals(sell_price) * quantity;
	let selectedCostPrice = roundTwoDecimals(selected_cost_price);
	let taxableSellPrice =
		productData.tax_type === ProductTaxType.disabled
			? 0
			: roundTwoDecimals(sell_price) * quantity;

	// check if bundle and has individual pricing,
	const isBundleProductWithIndividualPricing =
		product_type === ProductType.Bundle && show_individual_product_pricing;

	if (isBundleProductWithIndividualPricing) {
		// removing unselected optional products
		const validBundledProducts = (bundled_products || []).filter(a =>
			a.is_optional ? a.is_selected : true
		);
		// adding the bundled items setup fee to the product setup fee
		setupPriceTotal += validBundledProducts.reduce(
			(total, curr) => total + (curr.selected_setup_price || 0),
			0
		);

		// overriding the sell price tot he bundled item sell price.
		totalSellPrice = validBundledProducts.reduce(
			(total, curr) =>
				total +
				(roundTwoDecimals(curr.sell_price) || 0) * curr.quantity,
			0
		);

		taxableSellPrice = validBundledProducts.reduce((total, curr) => {
			if (curr.tax_type === ProductTaxType.disabled) {
				return total;
			}
			return (
				total + (roundTwoDecimals(curr.sell_price) || 0) * curr.quantity
			);
		}, 0);

		// overriding the sell price tot he bundled item sell price.
		selectedCostPrice = validBundledProducts.reduce(
			(total, curr) =>
				total +
				(roundTwoDecimals(curr.selected_cost_price) || 0) *
					curr.quantity,
			0
		);
	}

	const totalCostPrice = selectedCostPrice * quantity;

	const finalSellPrice = totalSellPrice - discount;
	taxableSellPrice = taxableSellPrice - discount;

	const subTotal = !!should_add_setup_price_to_margin
		? finalSellPrice + setupPriceTotal
		: finalSellPrice;

	const margin = Math.round((subTotal - totalCostPrice) * 100) / 100;
	const marginPercentage =
		(!!subTotal && Math.round((margin / subTotal) * 100 * 100) / 100) || 0;

	return {
		finalSellPrice: roundTwoDecimals(finalSellPrice),
		margin: roundTwoDecimals(margin),
		marginPercentage: roundTwoDecimals(marginPercentage),
		setupPriceTotal: roundTwoDecimals(setupPriceTotal),
		subTotal: roundTwoDecimals(subTotal),
		totalCostPrice: roundTwoDecimals(totalCostPrice),
		taxableSellPrice: roundTwoDecimals(taxableSellPrice),
		totalSellPrice: roundTwoDecimals(totalSellPrice)
	};
};

export const calculateBlockTotalWithoutTax = (blockData: QuoteBlockData) => {
	return blockData.total
		? blockData.total.one_time +
				blockData.total.annual +
				blockData.total.monthly +
				blockData.total.semi_annually +
				blockData.total.weekly +
				blockData.total.quarterly
		: 0;
};

export const calculateTax = (
	blockData: QuoteBlockData,
	document: Document,
	taxRegions: TaxRegion[],
	taxCategories: TaxCategory[]
): { tax: number; taxCategoryBreakdown: Record<string, TaxCodeLevel[]> } => {
	const selectedProducts: QuoteProduct[] =
		QuoteBlockData.getSelectedProducts(blockData);
	const validProducts = selectedProducts.filter(
		currentProduct => currentProduct.tax_category_id !== null
	);
	let totalTax = 0;
	const taxCategoryBreakdown: Record<string, TaxCodeLevel[]> = {};

	const taxRegionId =
		document?.tax_region_id ??
		taxRegions?.find(taxRegion => taxRegion.is_default)?.id;
	validProducts.forEach(product => {
		if (
			product.product_type === ProductType.Bundle &&
			product.show_individual_product_pricing
		) {
			const validBundledProducts = (
				product.bundled_products || []
			).filter(
				a => a.tax_category_id && (a.is_optional ? a.is_selected : true)
			);
			const validBundledProductsSetupPriceTotal =
				validBundledProducts.reduce(
					(total, curr) =>
						total +
						((curr.sell_price || 0) +
							(curr.selected_setup_price || 0)),
					0
				);
			validBundledProducts?.forEach(bundledProduct => {
				const taxCategory = taxCategories?.find(
					category => category.id === bundledProduct.tax_category_id
				);
				if (taxCategory && taxRegionId) {
					if (!taxCategoryBreakdown[taxCategory.id]) {
						taxCategoryBreakdown[taxCategory.id] =
							new Array<TaxCodeLevel>();
					}
					const taxCodes = taxCategory?.tax_codes?.filter(
						taxCode => taxCode?.region?.id === taxRegionId
					);
					const taxLevels = taxCodes
						?.map(taxCode => taxCode.levels)
						.reduce(
							(aggregate, taxCodeLevels) =>
								aggregate.concat(taxCodeLevels),
							new Array<TaxCodeLevel>()
						);
					let bundleProductSellPrice =
						(bundledProduct.sell_price || 0) *
							(bundledProduct.quantity || 0) +
						(bundledProduct.selected_setup_price || 0);
					const weightage_ratio = validBundledProductsSetupPriceTotal
						? bundleProductSellPrice /
						  validBundledProductsSetupPriceTotal
						: 0;
					bundleProductSellPrice =
						bundleProductSellPrice -
						weightage_ratio * (blockData.discount || 0) -
						weightage_ratio * (product.discount || 0);
					taxLevels?.forEach(taxLevel => {
						totalTax +=
							(taxLevel?.tax_percentage *
								bundleProductSellPrice) /
							100;
						const index = taxCategoryBreakdown[
							taxCategory.id
						].findIndex(level => level.name === taxLevel.name);
						if (index > -1) {
							taxCategoryBreakdown[taxCategory.id][
								index
							].tax_percentage +=
								(taxLevel?.tax_percentage *
									bundleProductSellPrice) /
								100;
						} else {
							taxCategoryBreakdown[taxCategory.id].push({
								name: taxLevel.name,
								tax_percentage:
									(taxLevel?.tax_percentage *
										bundleProductSellPrice) /
									100
							});
						}
					});
				}
			});
			const bundleTaxCategory = taxCategories?.find(
				category => category.id === product.tax_category_id
			);
			if (bundleTaxCategory && taxRegionId) {
				if (!taxCategoryBreakdown[bundleTaxCategory.id]) {
					taxCategoryBreakdown[bundleTaxCategory.id] =
						new Array<TaxCodeLevel>();
				}
				const bundleTaxCodes = bundleTaxCategory?.tax_codes?.filter(
					taxCode => taxCode?.region?.id === taxRegionId
				);
				const bundleTaxLevels = bundleTaxCodes
					?.map(taxCode => taxCode.levels)
					.reduce(
						(aggregate, taxCodeLevels) =>
							aggregate.concat(taxCodeLevels),
						new Array<TaxCodeLevel>()
					);
				const blockAdditionalPrice =
					(product.selected_setup_price || 0) -
					(product.selected_setup_discount || 0);
				bundleTaxLevels?.forEach(taxLevel => {
					totalTax +=
						(taxLevel?.tax_percentage * blockAdditionalPrice) / 100;
					const index = taxCategoryBreakdown[
						bundleTaxCategory.id
					].findIndex(level => level.name === taxLevel.name);
					if (index > -1) {
						taxCategoryBreakdown[bundleTaxCategory.id][
							index
						].tax_percentage +=
							(taxLevel?.tax_percentage * blockAdditionalPrice) /
							100;
					} else {
						taxCategoryBreakdown[bundleTaxCategory.id].push({
							name: taxLevel.name,
							tax_percentage:
								(taxLevel?.tax_percentage *
									blockAdditionalPrice) /
								100
						});
					}
				});
			}
		} else {
			const taxCategory = taxCategories?.find(
				category => category.id === product.tax_category_id
			);
			if (taxCategory && taxRegionId) {
				if (!taxCategoryBreakdown[taxCategory.id]) {
					taxCategoryBreakdown[taxCategory.id] =
						new Array<TaxCodeLevel>();
				}
				const taxCodes = taxCategory?.tax_codes?.filter(
					taxCode => taxCode?.region?.id === taxRegionId
				);
				const taxLevels = taxCodes
					?.map(taxCode => taxCode.levels)
					.reduce(
						(aggregate, taxCodeLevels) =>
							aggregate.concat(taxCodeLevels),
						new Array<TaxCodeLevel>()
					);
				const calculatedTotal =
					document?.tax_region_id &&
					document?.tax_region_id?.length > 0
						? calculateSubTotal(product)
						: calculateFlatTaxSubTotal(product);
				let { finalSellPrice } = calculatedTotal;
				const { setupPriceTotal } = calculatedTotal;
				// blockdData.total.grandTotal keeps recalculating tax, take separate values of all billing period
				const blockDataTotal = calculateBlockTotalWithoutTax(blockData);
				finalSellPrice =
					finalSellPrice +
					setupPriceTotal -
					getWeightedBlockDiscountForProduct(
						blockDataTotal,
						blockData.discount,
						finalSellPrice
					);
				taxLevels?.forEach(taxLevel => {
					totalTax +=
						(taxLevel?.tax_percentage * finalSellPrice) / 100;
					const index = taxCategoryBreakdown[
						taxCategory.id
					].findIndex(level => level.name === taxLevel.name);
					if (index > -1) {
						taxCategoryBreakdown[taxCategory.id][
							index
						].tax_percentage +=
							(taxLevel?.tax_percentage * finalSellPrice) / 100;
					} else {
						taxCategoryBreakdown[taxCategory.id].push({
							name: taxLevel.name,
							tax_percentage:
								(taxLevel?.tax_percentage * finalSellPrice) /
								100
						});
					}
				});
			}
		}
	});
	return {
		tax: roundTwoDecimals(totalTax),
		taxCategoryBreakdown
	};
};

export const calculateFlatTax = (blockData: QuoteBlockData): number => {
	const selectedProducts: QuoteProduct[] =
		QuoteBlockData.getSelectedProducts(blockData);

	const validCost = selectedProducts.reduce((costSum, currentProduct) => {
		const { taxableSellPrice } = calculateFlatTaxSubTotal(currentProduct);
		return costSum + taxableSellPrice;
	}, 0);

	const taxable = validCost - blockData?.discount;
	const totalTax = (taxable * blockData?.tax_percentage) / 100;

	return roundTwoDecimals(totalTax);
};

export const getWeightedBlockDiscountForProduct = (
	blockGrandTotal: number,
	blockDiscount: number,
	productSellPrice: number
): number => {
	if (!blockGrandTotal || !blockDiscount || !productSellPrice) {
		return 0;
	}
	const ratio = productSellPrice / blockGrandTotal;
	return ratio * blockDiscount;
};

export const productBillingTypeToGrandTotal: Record<
	ProductBillingType,
	keyof QuoteTotal
> = {
	[ProductBillingType.OneTime]: "one_time",
	[ProductBillingType.Weekly]: "weekly",
	[ProductBillingType.Monthly]: "monthly",
	[ProductBillingType.Annual]: "annual",
	[ProductBillingType.Quarterly]: "quarterly",
	[ProductBillingType.SemiAnnually]: "semi_annually"
};

export const calculateGrandTotal = (
	blockData: QuoteBlockData,
	document: Document,
	taxRegions: TaxRegion[],
	taxCategories: TaxCategory[]
): QuoteTotal => {
	if (!blockData || !blockData.sections || !blockData.sections.length) {
		return new QuoteTotal();
	}
	const selectedProducts: QuoteProduct[] =
		QuoteBlockData.getSelectedProducts(blockData);

	const totalObject: QuoteTotal = selectedProducts.reduce(
		(
			calculatedTotal: QuoteTotal,
			currentProduct: QuoteProduct
		): QuoteTotal => {
			const {
				selected_billing_period,
				show_individual_product_pricing,
				product_type,
				bundled_products = []
			} = currentProduct;

			const { setupPriceTotal, finalSellPrice } =
				document?.tax_region_id && document?.tax_region_id?.length > 0
					? calculateSubTotal(currentProduct)
					: calculateFlatTaxSubTotal(currentProduct);

			if (product_type !== ProductType.Bundle) {
				calculatedTotal[selected_billing_period] += finalSellPrice;
			} else {
				if (show_individual_product_pricing) {
					const validBundledProducts = (
						bundled_products || []
					).filter(a => (a.is_optional ? a.is_selected : true));

					validBundledProducts.forEach(bundledProduct => {
						const calculatedTotalSellPrice =
							(roundTwoDecimals(bundledProduct.sell_price) || 0) *
							(bundledProduct.quantity || 1);

						calculatedTotal[
							bundledProduct.selected_billing_period
						] += calculatedTotalSellPrice;

						// calculatedTotal.one_time += bundledProduct.selected_setup_price;
					});
				} else {
					// adding bundle total to the grand total
					calculatedTotal[selected_billing_period] += finalSellPrice;

					// adding totals of selected optional products
					const validBundledProducts = (
						bundled_products || []
					).filter(a => a.is_optional && a.is_selected);

					validBundledProducts.forEach(bundledProduct => {
						const calculatedTotalSellPrice =
							(roundTwoDecimals(bundledProduct.sell_price) || 0) *
							(bundledProduct.quantity || 1) *
							(currentProduct.quantity || 1);

						calculatedTotal[
							bundledProduct.selected_billing_period
						] += calculatedTotalSellPrice;

						calculatedTotal.one_time +=
							bundledProduct.selected_setup_price;
					});
				}
			}

			calculatedTotal.one_time += setupPriceTotal;

			return calculatedTotal;
		},
		new QuoteTotal()
	);

	totalObject.grand =
		totalObject.weekly +
		totalObject.annual +
		totalObject.monthly +
		totalObject.one_time +
		totalObject.quarterly +
		totalObject.semi_annually;

	totalObject.grand +=
		document?.tax_region_id && document?.tax_region_id?.length > 0
			? calculateTax(blockData, document, taxRegions, taxCategories)?.tax
			: calculateFlatTax(blockData);
	totalObject.grand -= blockData.discount;

	const result: QuoteTotal = {
		monthly: roundTwoDecimals(totalObject.monthly),
		weekly: roundTwoDecimals(totalObject.weekly),
		annual: roundTwoDecimals(totalObject.annual),
		one_time: roundTwoDecimals(totalObject.one_time),
		grand: roundTwoDecimals(totalObject.grand),
		quarterly: roundTwoDecimals(totalObject.quarterly),
		semi_annually: roundTwoDecimals(totalObject.semi_annually)
	};
	return result;
};

export const calculateGrandMargin = (
	blockData: QuoteBlockData,
	document: Document
): QuoteMargin => {
	if (!blockData || !blockData.sections || !blockData.sections.length) {
		return new QuoteMargin();
	}

	const selectedProducts: QuoteProduct[] =
		QuoteBlockData.getSelectedProducts(blockData);

	const totalMargin = selectedProducts.reduce(
		(
			calculatedMargin: QuoteMargin,
			currentProduct: QuoteProduct
		): QuoteMargin => {
			const { margin, totalCostPrice, subTotal } =
				document?.tax_region_id && document?.tax_region_id?.length > 0
					? calculateSubTotal(currentProduct)
					: calculateFlatTaxSubTotal(currentProduct);
			calculatedMargin.costPrice += totalCostPrice;
			calculatedMargin.margin += margin;
			calculatedMargin.sellPrice += subTotal;

			return calculatedMargin;
		},
		new QuoteMargin()
	);

	totalMargin.discount = roundTwoDecimals(blockData.discount);
	totalMargin.costPrice = roundTwoDecimals(totalMargin.costPrice);
	totalMargin.margin = roundTwoDecimals(totalMargin.margin);

	return totalMargin;
};

export const calculateSetupDiscount = (
	discountPercentage: number | undefined,
	setupPrice: number
) => {
	if (!discountPercentage) discountPercentage = 0;

	return roundTwoDecimals((setupPrice * discountPercentage) / 100);
};
